import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  SharedProductCard,
  useSharedItems,
  type SharedItem,
} from "../../../SharedProductCard"
import s from "./ProductSuggestions.module.scss"

type ProductSuggestionsProps = {
  productIds: string[]
  onOpen: (item: SharedItem) => void
  /** Told which ids are shown as cards, so the answer text can drop their links. */
  onShow?: (ids: string[]) => void
}

/**
 * The products the assistant suggested with an answer, as cards in suggestion
 * order. Ids the learner cannot open are left out, and a failed lookup shows
 * nothing: the answer text still names the products.
 */
export const ProductSuggestions = ({
  productIds,
  onOpen,
  onShow,
}: ProductSuggestionsProps) => {
  const { items, isError } = useSharedItems(productIds)

  // A string, so a refetch with the same cards does not report them again
  const shownIds = items?.map((item) => item.id).join(",") ?? ""
  useEffect(() => {
    onShow?.(shownIds ? shownIds.split(",") : [])
  }, [shownIds, onShow])

  if (isError && !items) return null

  return (
    <ProductSuggestionList
      items={items}
      resolvingCount={productIds.length}
      onOpen={onOpen}
    />
  )
}

export const ProductSuggestionList = ({
  items,
  resolvingCount,
  onOpen,
}: {
  /** `undefined` while the lookup is in flight. */
  items: SharedItem[] | undefined
  resolvingCount: number
  onOpen: (item: SharedItem) => void
}) => {
  const { t } = useTranslation()

  if (items?.length === 0) return null

  return (
    <ul
      className={s.productSuggestions}
      aria-label={t("ai_assistant_chat.SUGGESTED_PRODUCTS")}
    >
      {items
        ? items.map((item) => (
            <li key={item.id}>
              <SharedProductCard
                isOwn={false}
                state={{
                  status: "accessible",
                  item,
                  onOpen: () => onOpen(item),
                }}
              />
            </li>
          ))
        : Array.from({ length: resolvingCount }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholders
            <li key={index}>
              <SharedProductCard
                isOwn={false}
                state={{ status: "resolving" }}
              />
            </li>
          ))}
    </ul>
  )
}
